import { createAsyncThunk } from "@reduxjs/toolkit";

import { addBaggageToBookingInState } from "@repo/ui/api/addBaggageToBookingInState";
import buildAddBaggageInBookingPayload from "@repo/ui/helpers/buildAddBaggageInBookingPayload";
import { getTabSessionIdFromHeaders } from "@repo/ui/lib/getTabSessionIdFromHeaders";

import type { RootState } from "@repo/store/store";

import type { BaggageCart } from "../slices/baggageSlice";
import { setBookingDetails } from "../slices/bookingSlice";

export const confirmBaggageCart = createAsyncThunk<
  void,
  {
    prevBaggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
    baggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
    isUserStaff: boolean;
    currency: string;
  },
  { state: RootState }
>(
  "baggage/confirmBaggageCart",
  async (
    params: {
      prevBaggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
      baggageCart: { outbound?: BaggageCart; inbound?: BaggageCart };
      isUserStaff: boolean;
      currency: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state: RootState = getState();

    // If the same value skip this step
    if (JSON.stringify(params.prevBaggageCart) === JSON.stringify(params.baggageCart)) {
      return;
    }

    const ssrAvailability = state.booking.ssrAvailability;
    if (!ssrAvailability) throw new Error("SSRS not available");

    let tabSessionId: string | null = null;

    try {
      tabSessionId = await getTabSessionIdFromHeaders();
      if (!tabSessionId) return rejectWithValue("Tab session ID not found");
    } catch (error) {
      console.error("Error retrieving tab session ID:", error);
      return rejectWithValue(error);
    }

    const payload = buildAddBaggageInBookingPayload(
      ssrAvailability,
      params.baggageCart,
      params.currency,
      params.isUserStaff,
    );

    try {
      const addBaggageToBookingInStateResp = await addBaggageToBookingInState(
        payload,
        tabSessionId,
      );

      if (!addBaggageToBookingInStateResp.success) {
        throw new Error(
          addBaggageToBookingInStateResp.message ?? "Failed to add baggage to booking in-state",
        );
      }

      dispatch(setBookingDetails(addBaggageToBookingInStateResp.data ?? null));
    } catch (error) {
      console.log({ error });
      return rejectWithValue(error);
    }
  },
);
